<template>
  <FreeBottomSheet :name="name" @onClose="onFAQClose">
    <template #header="{ hide }">
      <FreeHeader class="p-4">
        <h2 class="text-base font-medium">{{ bsTitle }}</h2>
        <template #rightAction>
          <FreeCloseButton @click="hide" />
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <ul
        v-if="isGuideVisible"
        class="bg-background-neutral-secondary divide-situational-lightgrey mx-4 mb-4 divide-y rounded-2xl px-4"
      >
        <GuideItem
          v-for="(faq, idx) in config.faqItems"
          :key="idx"
          :name="getTitleTranslation(faq)"
          :text="getSummaryTranslation(faq)"
          @click="updateFAQItem(faq)"
        />
      </ul>
      <div v-else class="space-y-8 px-4">
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="bg-background-neutral-secondary text-text-neutral-secondary rounded-2xl px-4 py-3"
          v-html="selectedFAQItem.content"
        ></div>
        <!-- eslint-enable vue/no-v-html -->
        <FreeButton
          type="button"
          class="bg-situational-lightgrey sticky bottom-4 flex h-13 w-full justify-between"
          @click="isGuideVisible = true"
        >
          <span class="text-text-neutral-tertiary">{{
            t('faqBs.moreInfo')
          }}</span>
          <img
            src="../../../assets/images/chevron-arrow.svg"
            width="24"
            height="24"
            alt=""
          />
        </FreeButton>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { controllerModel } from 'shared';

  import messages from '../config/locales.json';
  import {
    FreeBottomSheet,
    FreeCloseButton,
    FreeHeader,
    FreeButton,
  } from '../../../shared/ui';
  import GuideItem from './GuideItem.vue';
  import { config, type types } from '../index';

  const props = defineProps<{ name: string }>();

  const { t } = useI18n({ messages });

  const isGuideVisible = ref(false);
  const selectedFAQ = ref<types.FAQItemType>();
  const bottomSheet = controllerModel.useBottomSheet();
  const params = computed(() => bottomSheet.getParams(props.name));

  const getFAQItemTranslationKey = (key: types.FAQItemType) =>
    `faqBs.items.${key}`;
  const getTitleTranslation = (key: types.FAQItemType) =>
    t(`${getFAQItemTranslationKey(key)}.title`);
  const getSummaryTranslation = (key: types.FAQItemType) =>
    t(`${getFAQItemTranslationKey(key)}.summary`);
  const getContentTranslation = (key: types.FAQItemType) =>
    t(`${getFAQItemTranslationKey(key)}.content`);

  const selectedFAQItem = computed(() => {
    const selectedFAQKey =
      selectedFAQ.value || (params.value.faqItemKey as types.FAQItemType);
    return {
      title: getTitleTranslation(selectedFAQKey),
      content: getContentTranslation(selectedFAQKey),
    };
  });

  const bsTitle = computed(() =>
    isGuideVisible.value ? t('faqBs.title') : selectedFAQItem.value.title,
  );

  const updateFAQItem = (key: types.FAQItemType) => {
    selectedFAQ.value = key;
    isGuideVisible.value = false;
  };

  const onFAQClose = () => {
    selectedFAQ.value = undefined;
    isGuideVisible.value = false;
  };
</script>
